import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { calcProPercentsBasedOnFilterAndDays } from "../../../../utilis/common";
import useDaysCard from "../../../../components/hooks/useDaysCard";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import CheckboxButton from "../../../../components/dashboard/shared/checkboxButton/CheckboxButton";
import styles from "./NinetyDaysAnalysis.module.scss";
import NinetyDaysAnalysisSkeleton from "./NinetyDaysAnalysisSkeleton";
import { PAGE_TYPE } from "../../../../types/pages.type";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import CheckboxLoader from "../../../../components/shared/checkbox-loader/CheckboxLoader";
import { Box, useMediaQuery } from "@mui/system";
import CheckboxCircle from "../../../../components/dashboard/shared/checkboxButton/CheckboxCircle";
import _ from "lodash";
import { useMemo, useCallback } from "react";
import TruncatedLabelWithTooltip from "./TruncatedLabelWithTooltip";
import CustomBar from "./CustomBar";

const defaultRadialDataArr = [
	{
		id: "Total",
		parentId: "total",
		color: "#92C7FF",
		data: [
			{
				x: "f",
				y: 0,
				percentage: 0,
				color: "#92C7FF",
			},
		],
	},
	{
		id: "0 - 7 Days",
		parentId: "a",
		color: "#21B1D0",
		data: [
			{
				x: "a",
				y: 0,
				percentage: 0,
				color: "#21B1D0",
			},
		],
	},
	{
		id: "8 - 14 Days",
		parentId: "b",
		color: "#5195DD",
		data: [
			{
				x: "b",
				y: 0,
				percentage: 0,
				color: "#5195DD",
			},
		],
	},
	{
		id: "15 - 30 Days",
		parentId: "c",
		color: "#4F80FF",
		data: [
			{
				x: "c",
				y: 0,
				percentage: 0,
				color: "#4F80FF",
			},
		],
	},
	{
		id: "31 - 60 Days",
		color: "#6B69DA",
		parentId: "d",
		data: [
			{
				x: "d",
				y: 0,
				percentage: 0,
				color: "#6B69DA",
			},
		],
	},
	{
		id: "61 - 90 Days",
		color: "#1D7CD3",
		parentId: "e",
		data: [
			{
				x: "e",
				y: 0,
				percentage: 0,
				color: "#1D7CD3",
			},
		],
	},
];

const defaultColors = [
	"#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6F91",
	"#845EC2", "#00C9A7", "#C34A36", "#F9C80E", "#FF9671",
	"#0081CF", "#2C73D2", "#FAD02E", "#00AF91", "#B76E79"
];

const NinetyDaysAnalysis = ({
	data,
	comparisonData,
	handleToggle,
	selectedItem = [],
	type,
	page,
	height,
	averageCensusComparison,
	averageCensus,
	filterListData,
	filterListDataComparison,
	totalTransfers,
	totalTransfersComparison,
	filter,
	filterComparison,
	cardTitle,
	reverseColors,
	loading,
	projectionDays,
	priorityNumber,
	transferType,
	isComparingAgainstAvgCensus,
	lockedTotalBy,
	spacialSelectedItem = [],
	isCustom: isCustomQuestion = false,
	question,
	isCustomTab = false,
	customTab = null,
	disableInteractions = false,
	hideRadialSection = false,
	/** Only Hospital header filter popover: remove extra gap under the chart (min-height + Nivo bottom margin) */
	headerFilterPopover = false,
}) => {
	const isMobile = useMediaQuery('(max-width:768px)');
	const defaultRadialData = useMemo(() => {
		if (isCustomQuestion) {
			return [...data].map((line, index) => {
				const reversedIndex = data.length - 1 - index;
				const colorItem = defaultColors[reversedIndex % defaultColors.length]
				return {
					id: line?._id,
					parentId: line?._id,
					color: colorItem,
					isPercentageVisible: line?.isPercentageVisible ?? true,
					data: [
						{
							x: line?._id,
							y: 0,
							percentage: 0,
							color: colorItem,
						},
					],
				};
			});
		} else {
			// Use data array to build radial data dynamically instead of static defaultRadialDataArr
			if (data && Array.isArray(data) && data.length > 0) {
				return [...data].map((line, index) => {
					const reversedIndex = data.length - 1 - index;
					const colorItem = defaultColors[reversedIndex % defaultColors.length];
					return {
						id: line?._id,
						parentId: line?._id,
						label: line?.label,
						color: colorItem,
						isPercentageVisible: line?.isPercentageVisible ?? true,
						data: [
							{
								x: line?._id,
								y: 0,
								percentage: 0,
								color: colorItem,
							},
						],
					};
				});
			}
			// Fall back to static default only if no data available
			return defaultRadialDataArr;
		}
	}, [data, isCustomQuestion]);

	const { dataList, dataListBarChart, dataListComparisonBarChart, radialDataList, radialDataComparisonList } =
		useDaysCard({
			data,
			comparisonData,
			defaultRadialData,
			page,
			averageCensusComparison,
			averageCensus,
			filterListData,
			filterListDataComparison,
			filter,
			filterComparison,
			totalTransfers,
			totalTransfersComparison,
			reverseColors,
			projectionDays,
			cardTitle
		});

	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();

	const isCustom = useMemo(() => {
		return isCustomQuestion
	}, [isCustomQuestion]);


	// Calculate chartHeight first (needed for isCustom margin calculation)
	const chartHeight = useMemo(() => {
		if (!dataListBarChart) return 0;
		let length = dataListBarChart.length;
		if (isCustom) {
			length = dataListBarChart?.length + 0.5;
		}

		if (length > 8) return length * 52;
		if (length > 4) return length * 54;
		if (length > 3) return length * 55;

		return length * 62;
	}, [dataListBarChart, isCustom]);

	// Calculate dynamic margin based on number of items and available height
	const calculateItemMargin = useMemo(() => {
		if (!dataList || dataList.length === 0) return 13;

		const itemCount = dataList.length;
		const itemHeight = 34; // Fixed height per item

		// Determine available height based on container
		// This should match the container height calculation and chartHeight logic
		let availableHeight;
		if (isMobile) {
			availableHeight = 290; // Mobile bar chart height
		} else if (isCustom) {
			// For isCustom mode, use chartHeight as the base
			// The checkbox list container has height = chartHeight and marginTop
			// So available height = chartHeight - listMarginTop
			// This must match the bar chart's availableBarHeight = chartHeight - topMargin - bottomMargin
			const listMarginTop = page === PAGE_TYPE.HOSPITAL ? 25 : (page === PAGE_TYPE.DECEASED || page === PAGE_TYPE.OVERALL ? -2 : 0);
			// Use chartHeight directly (which already accounts for the +0.5 for spacing)
			availableHeight = chartHeight - listMarginTop;
		} else if (headerFilterPopover && chartHeight > 0) {
			availableHeight = chartHeight + 20;
		} else {
			// Regular mode (not custom)
			// For calc(100% - 64px), estimate based on typical container heights
			if (itemCount <= 3) {
				availableHeight = 280;
			} else if (itemCount <= 5) {
				availableHeight = 350;
			} else if (itemCount <= 7) {
				availableHeight = 420;
			} else {
				availableHeight = 500;
			}
		}

		// Calculate margin: (availableHeight - totalItemHeights) / (itemCount + 1)
		// We use (itemCount + 1) because margins collapse between adjacent items
		// So we need: one top margin + (itemCount - 1) shared margins + one bottom margin = itemCount + 1 margins
		const totalItemHeights = itemCount * itemHeight;
		const totalMarginSpace = availableHeight - totalItemHeights;
		const calculatedMargin = totalMarginSpace > 0
			? Math.max(8, Math.min(20, totalMarginSpace / (itemCount + 1)))
			: 8; // Minimum margin if calculation is negative

		return Math.round(calculatedMargin);
	}, [dataList, isCustom, isMobile, page, chartHeight, headerFilterPopover]);

	const headerPopoverOuterStyle = useMemo(() => {
		if (!headerFilterPopover) return null;
		if (loading) return { minHeight: 0, height: 280 };
		if (chartHeight > 0) return { minHeight: 0, height: chartHeight + 38 };
		return { minHeight: 0 };
	}, [headerFilterPopover, loading, chartHeight]);

	// Calculate top margin for ResponsiveBar to align with first checkbox item
	// The first checkbox item starts at: listMarginTop + calculateItemMargin
	// The first bar should start at: containerMarginTop + ResponsiveBar topMargin
	// Since both containers have the same marginTop, ResponsiveBar just needs calculateItemMargin
	// Reduce topMargin to move bars up and align with checkbox labels
	const barTopMargin = useMemo(() => {
		return Math.max(0, calculateItemMargin - 10);
	}, [calculateItemMargin]);

	// Renderer for labels to be used inside Nivo Ticks - Memoized to prevent jumping on scroll
	const renderLabel = useCallback((line) => {
		const id = line?._id;
		const originalItem = data?.find((item) => item._id === id);
		const isSpacialItem = originalItem?.isSpacialItem ?? line?.isSpacialItem ?? false;
		const isPercentageVisible = originalItem?.isPercentageVisible ?? line?.isPercentageVisible ?? true;
		const labelId = `checkbox-analysis-list-label${id}`;
		const itemComparison = _.find(comparisonData, { _id: id });
		const colorItem = dataListComparisonBarChart.find((x) => x._id === id);

		return (
			<div className={`df aic ${styles.ninetyDaysLine} ${selectedItem.indexOf(id) !== -1 ? styles.ninetyDaysLineSelected : null}`}
				style={{ width: '130px', transform: 'scale(1)', transformOrigin: 'left center' }}
			>
				{!loading && colorItem?.comparisonColor && isPercentageVisible ? (
					<ColorBox
						type="div"
						color={colorItem?.comparisonColor}
						barWidth={colorItem?.width}
						comparingAgainst={colorItem?.value}
						comparingAgainstScaled={colorItem?.comparingAgainstScaled}
						numberOfDays={colorItem?.numberOfDays}
						sx={{ marginRight: "10px" }}
					/>
				) : (<Box sx={{ ml: 2.2 }}>&nbsp;</Box>)}
				<Box position="relative" display="inline-flex" alignItems="center">
					{isSpacialItem ? (
						<CheckboxCircle
							labelId={labelId}
							item={line}
							buttonText={<TruncatedLabelWithTooltip label={line.label} />}
							handleToggle={() => {
								if (disableInteractions) return;
								handleToggleWithLoader(() => handleToggle({
									item: line,
									type,
									itemComparison: itemComparison,
									isChecked: spacialSelectedItem?.indexOf(id) !== -1,
									cardTitle,
									isSpacialItem: line?.isSpacialItem ?? false,
									isCustomTab,
									customTab
								}), id)
							}}
							sx={{ ...(loadingItems[id] && { opacity: 0 }) }}
							checked={spacialSelectedItem?.indexOf(id) !== -1 || selectedItem.indexOf(id) !== -1}
							disabled={disableInteractions}
						/>
					) : (
						<CheckboxButton
							item={line}
							labelId={`checkbox-list-90-label-${id}`}
							checked={selectedItem.indexOf(id) !== -1}
							handleToggle={() => {
								if (!isSpacialItem && !disableInteractions) {
									handleToggleWithLoader(() => handleToggle({
										item: line,
										type,
										isChecked: selectedItem.indexOf(id) !== -1,
										cardTitle,
										question: isCustom
											? { isCustom: true, customQuestionInputType: question?.customQuestionInputType, isCustomTab, cardTitle, customTab }
											: null
									}), id)
								}
							}}
							buttonText={<TruncatedLabelWithTooltip label={line.label} />}
							disableRipple={true}
							sx={{ mr: "-2px", ...(loadingItems[id] && { opacity: 0 }) }}
							disabled={disableInteractions}
						/>
					)}
					<CheckboxLoader className={"small-dots-nineteen"} isLoading={loadingItems[id]} />
				</Box>
			</div>
		);
	}, [data, comparisonData, selectedItem, loading, dataListComparisonBarChart, disableInteractions, handleToggleWithLoader, handleToggle, type, spacialSelectedItem, cardTitle, isCustomTab, customTab, loadingItems, isCustom, question]);

	return loading ? (
		<NinetyDaysAnalysisSkeleton
			style={{
				...(headerFilterPopover ? { minHeight: 0, height: 280 } : { height: height || "100%" }),
			}}
		/>
	) : (
		<div
			className={`df aic w100 ${styles.ninetyDays}`}
			{...(headerPopoverOuterStyle
				? { style: headerPopoverOuterStyle }
				: height
					? { style: { height } }
					: {})}
		>
			<div
				className={`${styles.leftSec} ${(isCustomQuestion || hideRadialSection) && styles.customLeftSec} ${isCustomQuestion && styles.customQuestionLeftSec}`}
			>
				<div
					className={`${styles.itemsBarContainer}`}
					style={{
						height: (() => {
							if (isCustom && dataList?.length > 0) {
								if (dataList?.length >= 7) return "370px";
								return `${chartHeight}px`;
							}
							if (dataList?.length >= 6) return "370px";
							if (headerFilterPopover && chartHeight > 0 && dataList?.length > 0 && dataList?.length < 6) {
								return `${chartHeight + 32}px`;
							}
							return "calc(100% - 64px)";
						})(),
						overflowY: (dataList?.length >= 6 || (isCustom && dataList?.length >= 7)) ? "auto" : "visible",
					}}
				>
					<div
						className={styles.barChartContainer}
						style={{
							width: "100%",
							height:
								isCustom && dataList?.length >= 7
									? `${chartHeight}px`
									: dataList?.length >= 6
										? `${chartHeight}px`
										: headerFilterPopover && chartHeight > 0 && dataList?.length > 0 && dataList?.length < 6
											? `${chartHeight + 32}px`
											: "100%",
						}}
					>
						{dataListBarChart && dataListBarChart.length > 0 && (
							<CustomBar
								data={[...dataListBarChart].reverse().map(item => ({
									...item,
									key: item._id || item.key || item.label,
									originalLine: dataList.find(l => l._id === item._id || l.label === item.label)
								}))}
								height={chartHeight}
								isCustom={isCustom}
								priorityNumber={priorityNumber}
								defaultColors={defaultColors}
								filter={filter}
								projectionDays={projectionDays}
								isComparingAgainstAvgCensus={isComparingAgainstAvgCensus}
								lockedTotalBy={lockedTotalBy}
								transferType={transferType}
								calcProPercentsBasedOnFilterAndDays={calcProPercentsBasedOnFilterAndDays}
								itemMargin={calculateItemMargin}
								originalData={data}
								topMargin={barTopMargin}
								renderLabel={renderLabel}
								headerFilterPopover={headerFilterPopover}
							/>
						)}
					</div>
				</div>
			</div>

			{!isCustomQuestion && !isMobile && !hideRadialSection && (
				<div
					className=""
					style={{
						height: "250px",
						marginBottom: "58px",
						width: "37%",
					}}
				>
					{radialDataList.length > 0 && (
						<ResponsiveRadialBar
							data={radialDataList}
							padding={0.4}
							cornerRadius={50}
							margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
							radialAxisStart={null}
							endAngle={360}
							enableTracks={false}
							enableRadialGrid={false}
							enableCircularGrid={false}
							colors={(d) => d.data.color}
							axisLeft={null}
							axisBottom={null}
							circularAxisOuter={null}
							enableArcLabels={false}
							enableArcLinkLabels={false}
						/>
					)}
					<div className={`${styles.radialLegend}`}>
						{radialDataList.length > 0 &&
							radialDataList.map((line, index) => {
								const comparisonItem = radialDataComparisonList.find((x) => line.id === x.id);

								const originalItem = data?.find((item) => item._id === line.id);
								const isPercentageVisible = originalItem?.isPercentageVisible ?? true;
								return (
									<span className={`df aic fs10 fw600 ffint`} key={`line${index}`}>
										<div className={`${styles.icon}`} style={{ backgroundColor: line.color }}></div>
										{!loading && isPercentageVisible && (
											<ColorBox
												color={comparisonItem?.data[0]?.comparisonColor || null}
												sx={{ height: "13px !important", ml: "-2px", width: "9px", mr: "1px" }}
												comparingAgainst={comparisonItem?.data[0]?.y}
												comparingAgainstScaled={comparisonItem?.data[0]?.comparingAgainstScaled}
												numberOfDays={comparisonItem?.data[0]?.numberOfDays}
											/>
										)}
										{line?.label || line.id}: {line.data[0].y}
									</span>
								);
							})}
					</div>
				</div>
			)}
		</div>
	);
};

export default NinetyDaysAnalysis;
